import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface RadioOption<T extends string | number> {
    value: T;
    label: string;
    /** Spoken instead of `label` when the bare label is ambiguous, e.g. "1". */
    srLabel?: string;
}

interface RadioGroupFieldProps<T extends string | number> {
    /** The question. Becomes the group's accessible name. */
    legend: string;
    /** Groups the radios for the browser, so arrow keys stay inside this row. */
    name: string;
    options: readonly RadioOption<T>[];
    value: T | null;
    onChange: (value: T) => void;
    error?: string;
    /** Guidance under the options, e.g. "1 = no pain, 10 = worst pain". */
    hint?: string;
    className?: string;
    /**
     * Attached to the first option so react-hook-form's `setFocus` can move the
     * user to this row when validation fails. Focusing the group's first radio
     * is right even when a later one would be chosen: an empty group has no
     * checked option to focus.
     */
    inputRef?: React.Ref<HTMLInputElement>;
    /**
     * 'grid' for equal-width options (the numeric scales), 'flow' for word
     * labels of differing lengths. See the layout comment below.
     */
    layout?: 'grid' | 'flow';
}

/**
 * A single-choice row built on native radio inputs.
 *
 * Native radios rather than a custom widget on purpose: the browser already
 * gives a radio group one tab stop, arrow-key movement between options, and the
 * right announcement from a screen reader. A div-with-role reimplementation
 * would be more code and less correct. The input is only visually hidden, never
 * `display: none`, so it keeps focus and hit-testing; the styled span is its
 * `peer`, which is how the focus ring survives the hiding.
 *
 * Every option is at least 44px tall (#75): this audience taps with a thumb.
 */
export function RadioGroupField<T extends string | number>({
    legend,
    name,
    options,
    value,
    onChange,
    error,
    hint,
    className,
    inputRef,
    layout = 'grid',
}: RadioGroupFieldProps<T>) {
    const legendId = useId();
    const hintId = useId();
    const errorId = useId();

    const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

    return (
        <fieldset
            role="radiogroup"
            aria-labelledby={legendId}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : undefined}
            className={cn('space-y-2', className)}
        >
            {/* A `legend` inside a styled flex/grid container behaves oddly across
                browsers, so the accessible name is wired explicitly by id and the
                element itself stays a plain block. */}
            <legend id={legendId} className="text-base font-medium text-gray-900">
                {legend}
            </legend>

            {/* Two layouts, because the two kinds of option have opposite needs.
                Numbers are all the same width, so an even five-column grid is
                tidiest and lets 1-10 wrap to two rows of five. Words are not:
                "Sometimes" is nearly twice "Often", and five equal columns
                cannot hold it at 360px without dropping the text to about 10px.
                Measured, not guessed — it overflowed its box on a real phone
                viewport. So words size to their content and merely share out
                what is left over, with a 44px floor for the short ones. */}
            <div
                className={cn(
                    'gap-1 sm:gap-1.5',
                    layout === 'grid' ? 'grid grid-cols-5' : 'flex flex-wrap',
                )}
            >
                {options.map((option, index) => {
                    const checked = value === option.value;
                    return (
                        <label
                            key={option.value}
                            className={cn(
                                'cursor-pointer text-center',
                                layout === 'flow' && 'min-w-11 flex-auto',
                            )}
                            data-testid={`${name}-${option.value}`}
                        >
                            <input
                                type="radio"
                                name={name}
                                value={option.value}
                                checked={checked}
                                onChange={() => onChange(option.value)}
                                aria-label={option.srLabel}
                                ref={index === 0 ? inputRef : undefined}
                                className="sr-only peer"
                            />
                            <span
                                className={cn(
                                    'flex min-h-11 w-full items-center justify-center rounded-lg border py-2',
                                    'leading-tight transition-colors',
                                    // The five words only sit on one line at a
                                    // phone width if they are a size down; at
                                    // text-sm "Always" wraps to a row of its
                                    // own and every question grows by 50px.
                                    layout === 'flow'
                                        ? 'px-1 text-xs sm:px-1.5 sm:text-sm'
                                        : 'px-1.5 text-sm',
                                    'peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-600 peer-focus-visible:ring-offset-2',
                                    checked
                                        ? // emerald-700, not the 600 used elsewhere
                                          // in the app: white on 600 is 3.8:1,
                                          // under the 4.5:1 AA needs at this size.
                                          // 700 measures 5.5:1. #241 is unresolved
                                          // precisely because nobody checked.
                                          'border-emerald-700 bg-emerald-700 font-semibold text-white'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-emerald-400 hover:bg-emerald-50',
                                )}
                            >
                                {option.label}
                            </span>
                        </label>
                    );
                })}
            </div>

            {hint && (
                <p id={hintId} className="text-sm text-gray-500">
                    {hint}
                </p>
            )}
            {error && (
                <p id={errorId} className="text-sm font-medium text-red-700">
                    {error}
                </p>
            )}
        </fieldset>
    );
}

/** 1-10, laid out two rows of five so each option stays thumb-sized on a phone. */
export const SCALE_1_TO_10: readonly RadioOption<number>[] = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
}));

/** 1-5, for the commitment and effort question. */
export const SCALE_1_TO_5: readonly RadioOption<number>[] = Array.from({ length: 5 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
}));
